import React, { useEffect, useRef, useState } from 'react';
import { CameraView, useCameraPermissions } from 'expo-camera';
import Tesseract from 'tesseract.js';
import {
  Animated,
  StyleSheet,
  Platform,
  View,
  Easing,
  ActivityIndicator,
  Image as Img,
} from 'react-native';
import { colors } from '_/config/theme';
import Button from '../Button';
import Text from '../Text';
import ModalBackground from '../ModalBackground';
import { useTranslation } from 'react-i18next';
import { PrimaryKeyType } from '@w3lcome/types';
import { showMessage } from 'react-native-flash-message';
// import { cpf } from 'cpf-cnpj-validator';
import * as ImageManipulator from 'expo-image-manipulator';
import { getBase64String } from '_/helpers/getBase64String';

interface WebOCRProps {
  onClose: () => void;
  onScan: (data: {
    cpf?: string | null;
    rg?: string | null;
    name?: string | null;
    documentOcrPicture?: string;
  }) => void;
  isVisible: boolean;
  saveOcrPicture: boolean;
  documentPrimaryKeyType?: PrimaryKeyType;
}

export default function WebOCR({
  isVisible,
  onClose,
  documentPrimaryKeyType,
  onScan,
  saveOcrPicture,
}: WebOCRProps) {
  const cameraRef = useRef<CameraView>(null);
  const { t } = useTranslation();
  const animation = useRef(new Animated.Value(0));
  const [permission, requestPermission] = useCameraPermissions();
  const scanningRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const [preparingScan, setPreparingScan] = useState(false);

  const [previewUri, setPreviewUri] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (permission?.status !== 'granted') {
        const permission = await requestPermission();
        if (permission.status !== 'granted') {
          showMessage({
            message: t('error'),
            description: t('pictureScreen.CameraPermissionDenied'),
            backgroundColor: colors.danger,
          });
          return;
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (isVisible && permission?.granted && !scanningRef.current) {
      setPreparingScan(true);

      const timer = setTimeout(() => {
        setPreparingScan(false);
        startAutoScan();
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      stopAutoScan();
      setPreparingScan(false);
    }
  }, [isVisible, permission?.granted]);

  const MAX_ATTEMPTS = 5;

  const startAutoScan = async () => {
    scanningRef.current = true;
    let attempts = 0;

    while (scanningRef.current && attempts < MAX_ATTEMPTS) {
      attempts++;
      setLoading(true);

      try {
        const photo = await cameraRef.current?.takePictureAsync({
          base64: true,
          skipProcessing: true,
        });
        if (!photo?.base64) continue;

        const processed = await preprocessImage(photo.uri);

        setPreviewUri(processed.base64);

        const { data } = await Tesseract.recognize(processed.base64!, 'por');

        console.log(data.text);

        const { cpf: cpfMatch, rg: rgMatch, name } = extractCPFandRG(data.text);

        console.log('cpfMatch ' + cpfMatch);
        console.log('rgMatch ' + rgMatch);

        if (documentPrimaryKeyType) {
          if (documentPrimaryKeyType === PrimaryKeyType.CPF && cpfMatch) {
            handleSuccess(photo.uri, cpfMatch, name, rgMatch);
            break;
          }
          if (documentPrimaryKeyType === PrimaryKeyType.RG && rgMatch) {
            handleSuccess(photo.uri, cpfMatch, name, rgMatch);
            break;
          }
        } else if (cpfMatch || rgMatch) {
          handleSuccess(photo.uri, cpfMatch, name, rgMatch);
          break;
        }
      } catch (err) {
        console.error('Erro no OCR:', err);
      }

      setLoading(false);
      await new Promise((res) => setTimeout(res, 2000));
    }

    if (scanningRef.current && attempts >= MAX_ATTEMPTS) {
      showMessage({
        message: `Não foi possível ler o documento após ${MAX_ATTEMPTS} tentativas.`,
        backgroundColor: colors.danger,
      });
      onClose();
    }

    stopAutoScan();
  };

  const handleSuccess = async (
    uri: string,
    cpf?: string | null,
    name?: string | null,
    rg?: string | null
  ) => {
    const flippedPicture = await ImageManipulator.manipulateAsync(
      uri,
      [{ flip: ImageManipulator.FlipType.Horizontal }],
      { compress: 1, base64: true }
    );

    console.log('cpf ' + cpf);
    console.log('rg ' + rg);
    console.log('name ' + name);

    onScan({
      cpf,
      rg,
      name,
      ...(saveOcrPicture && {
        documentOcrPicture: getBase64String(flippedPicture.base64),
      }),
    });
    stopAutoScan();
    onClose();
  };

  const stopAutoScan = () => {
    scanningRef.current = false;
    setLoading(false);
  };

  const preprocessImage = async (uri: string) => {
    const resized = await ImageManipulator.manipulateAsync(uri, [{ resize: { width: 1200 } }], {
      base64: true,
    });

    return new Promise<{ base64: string }>((resolve) => {
      const img = new window.Image();
      img.src = resized.uri;
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        const contrastFactor = 1.6; // aumento de contraste mais forte
        const brightnessOffset = -20; // escurece levemente o fundo

        for (let i = 0; i < data.length; i += 4) {
          // Tons de cinza
          let gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

          // Aplica contraste e brilho
          gray = (gray - 128) * contrastFactor + 128 + brightnessOffset;

          // Limita valores
          gray = Math.max(0, Math.min(255, gray));

          data[i] = data[i + 1] = data[i + 2] = gray;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve({ base64: canvas.toDataURL('image/jpeg') });
      };
      img.onerror = (err) => {
        console.error('Erro ao carregar imagem no canvas:', err);
      };
    });
  };

  const extractCPFandRG = (text: string) => {
    const normalized = normalizeText(text);

    console.log('----------- normalized ------------');
    console.log(normalized);

    const cpfRegex = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
    const rgRegex = /\b\d{1,2}\.?\d{3}\.?\d{3}(?:-?[0-9Xx])?\b/g;

    const cpfMatches = normalized.match(cpfRegex);
    const rgMatches = normalized.match(rgRegex);

    console.log('cpfMatches ' + cpfMatches);

    const name = extractNameSmart(normalized);

    const cpfCandidate = cpfMatches ? cpfMatches[0].replace(/\s/g, '') : null;
    // const validCPF = cpfCandidate && cpf.isValid(cpfCandidate) ? cpfCandidate : null;

    const rgCandidate = rgMatches ? rgMatches[0].replace(/\s/g, '') : null;

    return {
      cpf: cpfCandidate,
      rg: rgCandidate,
      name,
    };
  };

  function extractNameSmart(text: string): string | null {
    if (isCNHText(text)) {
      return extractNameFromCNH(text);
    }

    return extractNameFromRG(text);
  }

  function isCNHText(text: string): boolean {
    const upper = text.toUpperCase();
    return (
      upper.includes('CARTEIRA NACIONAL DE HABILITAÇÃO') ||
      upper.includes('DRIVER LICENSE') ||
      upper.includes('PERMISO DE CONDUCCIÓN') ||
      upper.includes('CATEGORIA') ||
      upper.includes('1ª HABILITAÇÃO') ||
      upper.includes('NOME E SOBRENOME')
    );
  }

  function extractNameFromCNH(text: string): string | null {
    // Normaliza texto: maiúsculas, remove caracteres estranhos que atrapalham
    const normalizedText = text.toUpperCase().replace(/[^A-ZÀ-Ý\s]/g, ' ');

    // Busca o índice da âncora
    const anchorRegex = /NOME\s+E\s+SOBRENOME/;
    const anchorMatch = normalizedText.match(anchorRegex);

    if (!anchorMatch || anchorMatch.index === undefined) return null;

    // Pega o texto logo depois da âncora
    const startIndex = anchorMatch.index + anchorMatch[0].length;
    const afterAnchor = normalizedText.slice(startIndex).trim();

    // Pega até 7 palavras após o anchor (limite arbitrário para nome)
    const words = afterAnchor.split(/\s+/).filter(Boolean);
    const nomePalavras = words.slice(0, 7);

    // Se tem menos de 2 palavras, considera inválido
    if (nomePalavras.length < 2) return null;

    // Junta as palavras para formar o nome
    return nomePalavras.join(' ');
  }

  function extractNameFromRG(text: string): string | null {
    // Regex para capturar nome entre << >> (caso comum)
    const betweenSymbols = /[<≤]{1,2}\s*([A-ZÀ-Ý\s]{8,})\s*[>»]{1,2}/i;
    const matchSymbols = text.match(betweenSymbols);
    if (matchSymbols) {
      const candidate = matchSymbols[1].trim();
      if (isValidName(candidate)) return candidate;
    }

    // Regex para capturar nome após "NOME" (case insensitive)
    const afterName = /NOME\s+([A-ZÀ-Ý][A-Za-zÀ-ÿ\s]{5,})/i;
    const matchAfterName = text.match(afterName);
    if (matchAfterName) {
      const candidate = matchAfterName[1].trim().split(/\s+/).slice(0, 6).join(' ');
      if (isValidName(candidate)) return candidate;
    }

    // Regex fallback para nomes em caixa alta (3 a 6 palavras)
    const uppercaseFallback = /\b([A-ZÀ-Ý]{2,}(?:\s+[A-ZÀ-Ý]{2,}){2,5})\b/;
    const matchUppercase = text.match(uppercaseFallback);
    if (matchUppercase) {
      const candidate = matchUppercase[1].trim();
      if (isValidName(candidate)) return candidate;
    }

    return null;
  }

  function isValidName(name: string): boolean {
    const blacklist = [
      'VALIDA',
      'VÁLIDA',
      'TERRITORIO',
      'TERRITÓRIO',
      'NACIONAL',
      'REGISTRO',
      'DATA',
      'EXPEDICAO',
      'NATURALIDADE',
      'FILIAÇÃO',
      'DIRETOR',
      'GERENTE',
      'LEI',
      'ORIGEM',
      'DOCUMENTO',
      'IDENTIDADE',
      'BRASIL',
      'MINISTERIO',
      'TRANSPORTE',
      'EM TODO',
      'TODO O',
      'PERMISO',
      'CONDUCCION',
      'NASCIMENTO',
      'DRIVER',
      'LICENSE',
      'CARTEIRA',
      'SECRETARIA',
      'FEDERATIVA',
      'TRANSPORTES',
      'ESTADO',
    ];
    const upperName = name.toUpperCase();
    if (blacklist.some((word) => upperName.includes(word))) return false;

    const words = name.trim().split(/\s+/);
    if (words.length < 2 || words.length > 6) return false;

    return true;
  }

  const normalizeText = (text: string) =>
    text
      .replace(/,/g, '.') // converte vírgula em ponto
      .replace(/\n/g, ' ') // quebra de linha → espaço
      .replace(/(\d)\s+(\d)/g, '$1$2') // cola números separados por espaço
      .replace(/[^\dA-Za-z.\-/]/g, ' ') // remove tudo exceto letras, números, ponto, traço, barra
      .replace(/\s+/g, ' ') // colapsa espaços múltiplos
      .trim();

  if (!isVisible) return null;

  useEffect(() => {
    Animated.timing(animation.current, {
      duration: 400,
      toValue: isVisible ? 1 : 0,
      easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <ModalBackground isVisible={isVisible} onClose={onClose}>
      <Text
        style={{
          opacity: animation.current.interpolate({
            inputRange: [0, 0.4, 1],
            outputRange: [0, 0, 1],
            extrapolate: 'clamp',
          }),
          marginBottom: 10,
        }}
        center
        size={20}
        fontType="bold"
      >
        Posicione o documento para validação
      </Text>

      <Animated.View
        style={{
          marginBottom: 15,
          opacity: animation.current.interpolate({
            inputRange: [0, 0.4, 1],
            outputRange: [0, 0, 1],
            extrapolate: 'clamp',
          }),
        }}
      >
        <Text size={16} style={{ marginBottom: 4 }}>
          • Segure o documento dentro da área destacada.
        </Text>
        <Text size={16} style={{ marginBottom: 4 }}>
          • Certifique-se de que a iluminação seja adequada.
        </Text>
        <Text size={16} style={{ marginBottom: 4 }}>
          • Evite reflexos ou sombras sobre o documento.
        </Text>
        <Text size={16} style={{ marginBottom: 4 }}>
          • Mantenha o documento estável até a validação ser concluída.
        </Text>
      </Animated.View>

      {preparingScan && (
        <View>
          <Text size={18} fontType="bold" color={colors.primary}>
            Preparando para validar o documento...
          </Text>
        </View>
      )}

      <Animated.View
        style={[
          styles.cameraContainer,
          {
            opacity: animation.current.interpolate({
              inputRange: [0, 0.4, 1],
              outputRange: [0, 0, 1],
              extrapolate: 'clamp',
            }),
          },
        ]}
      >
        {permission?.granted ? (
          <CameraView ref={cameraRef} style={{ width: 500, height: 300 }} facing="front" />
        ) : (
          <View style={styles.cameraDenied}>
            <Text center size={24} color={colors.white}>
              {!permission?.granted ? t('qrCodeModal.permissionDenied') : t('Processing...')}
            </Text>
          </View>
        )}
        <View style={styles.overlayBox} />
      </Animated.View>

      {previewUri && (
        <Img
          source={{ uri: previewUri }}
          style={{ width: 350, height: 200, marginBottom: 10, borderRadius: 8 }}
          resizeMode="contain"
        />
      )}

      <View style={styles.footerContainer}>
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} />
        ) : (
          <Button leftIcon="x" large onPress={onClose} outline>
            {t('qrCodeModal.closeCamera')}
          </Button>
        )}
      </View>
    </ModalBackground>
  );
}

const styles = StyleSheet.create({
  cameraContainer: {
    marginVertical: 20,
    width: 500,
    height: 350,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  overlayBox: {
    position: 'absolute',
    borderColor: colors.primary,
    borderWidth: 2,
    top: 30,
    left: 30,
    right: 30,
    bottom: 30,
    borderRadius: 8,
  },
  cameraDenied: {
    width: 350,
    height: 220,
    backgroundColor: colors.dark2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerContainer: {
    height: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
